import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that manages the cart drawer upsell add-to-cart action.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<CartUpsellRefs>
 */
export class CartUpsellComponent extends Component {
  requiredRefs = ['addToCartButton'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.addToCartButton.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.addToCartButton.removeEventListener('click', this.handleClick);
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const { addToCartButton } = this.refs;

    if (addToCartButton.disabled) return;

    addToCartButton.disabled = true;
    addToCartButton.dataset.loading = 'true';

    const variantId = addToCartButton.dataset.variantId;
    const quantity = Number(addToCartButton.dataset.quantity) || 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: variantId,
      quantity,
      sections: cartItemComponentsSectionIds.join(','),
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-upsell',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('json', { body });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'cart-upsell-component',
                  sourceId: this.id?.toString(),
                  itemCount: quantity,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          return;
        }

        const ajaxCart = await this.#refreshCart();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'cart-upsell-component',
            sourceId: this.id?.toString(),
            itemCount: quantity,
            sections: response.sections,
            didError: false,
          },
        });

        // The upsell card removes itself once its product is in the cart,
        // since the drawer section markup re-renders from the returned sections.
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        addToCartButton.disabled = false;
        delete addToCartButton.dataset.loading;
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  };

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsellComponent);
}